/* eslint-disable @typescript-eslint/no-explicit-any */
import { Store, Commit } from 'vuex'
import { Line, CategoriaDeLotesDaFolha, EmpregadoId, DataEmFormatoISO, Linha } from '@/tipos'
import {
  DadosAninhadosParaCarregamento,
  DatasLimitesRequisicao,
  ICarregadoraDeLotesFolha,
  IPayloadDiasDaFolhaPorEntidade,
  IStateFolha,
  ParametrosDeRequisicaoAninhada,
  PayloadLoteDeCarregamento
} from '@/typescript/modulos/estrutura-de-pontos-da-empresa/Folha/dominio/folha.interface'
import { montarItemsDaListaEmBlocos, formatarDataParaEnvio } from '@/utils/formatadoras/formatadoras'
import moment from 'moment'
import { FiltradorDeErros } from '@/erros'
import { IFolhaRepositorio, SubRotaFolhaParcial } from '@/typescript/modulos/estrutura-de-pontos-da-empresa/Folha/repositorio/folha.repositorio.interface'
import { IRequestDiasDaFolhaPorEntidade, IRequestFolhaParcialSubRotaBase, IRequestGerarJornadasQueFaltam, IRequestLoteDeJustificativasDasLinhas, IRequestRegerarJornadasQuePrecisam, IRequestVerificarDia } from '@/typescript/modulos/estrutura-de-pontos-da-empresa/Folha/repositorio/folha.respositorio.request'
import { FolhaRepositorio } from '../repositorio/folha.repositorio'
import { ServicoAbstrato } from '@/typescript/servicos/servicoAbstrato/servicoAbstrato.servico'
import { transformarDadosDaNovaApiParaAntiga } from './dto'
import { EstadosLinhaParaRecalcularEnum } from './folha.enum'
import { DiaDaFolhaDados, DiaSemDiaDaFolha } from './linha.interface'
import { modalDeErro } from '@/utils'
import i18n from '@/typescript/servicos/i18n/i18n.servico'
import { agruparDiasPorEmpregado, gerarChaveLinhaRecalculo, jornadaPrecisaSerRegerada, transformarDetalhesDeApontamentos } from './folha.utils'
import { CanceledError } from 'axios'
import store from '@/store'

/**
 * Evita gravar startDate/endDate null no header quando a API retorna lista vazia ou o DTO não tem bordas do período.
 */
function obterLimitesPeriodoParaHeader (
  inicioPeriodo: string | null,
  fimPeriodo: string | null,
  dia: number | undefined,
  mes: number | undefined,
  ano: number | undefined,
  dataDoPeriodoHeader: Date | string | null | undefined
): { inicio: string; fim: string } {
  if (inicioPeriodo && fimPeriodo) {
    return { inicio: inicioPeriodo, fim: fimPeriodo }
  }
  let base = mes != null && ano != null
    ? moment({ year: ano, month: mes - 1, day: dia != null && dia >= 1 && dia <= 31 ? dia : 1 })
    : moment(dataDoPeriodoHeader)
  if (!base.isValid()) {
    base = moment()
  }
  const inicio =
    inicioPeriodo ??
    (fimPeriodo ? moment(fimPeriodo).clone().startOf('month').format('YYYY-MM-DD') : base.clone().startOf('month').format('YYYY-MM-DD'))
  const fim =
    fimPeriodo ??
    (inicioPeriodo ? moment(inicioPeriodo).clone().endOf('month').format('YYYY-MM-DD') : base.clone().endOf('month').format('YYYY-MM-DD'))
  return { inicio, fim }
}

const criarControladorDeCancelamentoDeRequisicao = () => {
  return new AbortController()
}

type FuncaoMapeadora = {
  djs: (linhas: Line[]) => { djs: string[] };
  pts: (linhas: Line[]) => void;
  tms: (linhas: Line[]) => void;
  justifies: (linhas: Line[]) => void;
  whbs: (linhas: Line[]) => void;
  approvals: (linhas: Line[]) => void;
};

/**
 * Classe responsável por carregar lotes de dados da folha. São dados necessarios para exibição de informações das linhas.
 * São eles: Apontamentos, aprovações, marcações, banco de horas e justificativas.
 * São carregados após o carregamento da API de linhas que não vem com todas essas informações a fim de carregar parcialmente.
 * @implements {ICarregadoraDeLotesFolha}
 */
export class CarregadoraDeLotesFolha implements ICarregadoraDeLotesFolha {
  repositorioFolha: IFolhaRepositorio;
  commit: Commit;
  state: { lines: Line[], requisicoes: any[] };
  categoriaSendoCarregada: CategoriaDeLotesDaFolha;
  dadosAninhados: DadosAninhadosParaCarregamento[];
  quantidadeDeItensNoLote: number;
  dataInicial: string;
  dataFinal: string;
  mergearMarcacoes: boolean;

  /**
 * @param {IFolhaRepositorio} repositorioFolha - O repositório de folha.
 * @param {Commit} commit - A função de commit para mutações de estado.
 * @param {{ lines: Line[], requisicoes: any[] }} state - O estado atual contendo linhas e requisições.
 * @param {CategoriaDeLotesDaFolha} categoriaSendoCarregada - A categoria de lotes da folha a ser carregada.
 * @param {DadosAninhadosParaCarregamento[]} dadosAninhados - Dados aninhados para carregamento.
 * @param {number} quantidadeDeItensNoLote - A quantidade de itens por lote.
 * @param {string} dataInicial - A data inicial do período de carregamento.
 * @param {string} dataFinal - A data final do período de carregamento.
 * @param {boolean} [mergearMarcacoes=false] - Se deve mesclar marcações.
 */
  constructor (
    repositorioFolha: IFolhaRepositorio,
    commit: Commit,
    state: { lines: Line[], requisicoes: any[] },
    categoriaSendoCarregada: CategoriaDeLotesDaFolha,
    dadosAninhados: DadosAninhadosParaCarregamento[],
    quantidadeDeItensNoLote: number,
    dataInicial: string,
    dataFinal: string,
    mergearMarcacoes = false
  ) {
    this.repositorioFolha = repositorioFolha
    this.commit = commit
    this.state = state
    this.categoriaSendoCarregada = categoriaSendoCarregada
    this.dadosAninhados = dadosAninhados || []
    this.quantidadeDeItensNoLote = quantidadeDeItensNoLote
    this.dataInicial = dataInicial
    this.dataFinal = dataFinal
    this.mergearMarcacoes = mergearMarcacoes
  }

  /**
 * Inicia o processo de carregamento dos lotes executando todos os processos necessários para o carregamento em lotes
 * @returns {Promise<void>}
 */
  async carregar () {
    this.iniciarEstadoDeCarregamento()
    const listaDeIdsDosEmpregados: string[] = CarregadoraDeLotesFolha.obterIdsUnicosDeEmpregados(this.state.lines)
    const parametrosParaRequisitar: DatasLimitesRequisicao = this.formatarParametrosParaRequisicao()
    const lotesDeCarregamento: string[][] = CarregadoraDeLotesFolha.definirLotesDeCarregamento(listaDeIdsDosEmpregados, this.quantidadeDeItensNoLote)
    const payloadPrimeiroLote: PayloadLoteDeCarregamento | DatasLimitesRequisicao = await this.carregarPrimeiroLote(parametrosParaRequisitar, lotesDeCarregamento)
    await this.carregarDemaisLotes(payloadPrimeiroLote, lotesDeCarregamento)
    await Promise.allSettled(this.state.requisicoes)
    this.pararEstadoDeCarregamento()
  }

  /**
 * Registra uma requisição com commit.
 * @param {Record<string, unknown>} payload - O payload da requisição.
 * @param {boolean} mergearMarcacoes - Se deve mesclar marcações.
 * @param {CategoriaDeLotesDaFolha} categoriaACarregar - A categoria de lotes a carregar.
 * @returns {Promise<void>}
 */
  private async registrarRequisicaoCommitando (
    payload: Record<string, unknown>,
    mergearMarcacoes: boolean,
    categoriaACarregar: CategoriaDeLotesDaFolha
  ) {
    const requisicao = this.tentarFazerRequisicao({ load: categoriaACarregar }, payload)
    requisicao.then((resultado) => {
      this.commit('_populateLines', {
        calcLinesMap: categoriaACarregar === 'pts',
        lines: resultado.data.success.lines,
        loaded: categoriaACarregar,
        mergeTms: mergearMarcacoes
      })
    })
    this.commit('adicionarRequisicao', requisicao)
  }

  /**
 * Tenta fazer uma requisição para a rota con
 * @param {Record<string, unknown>} parametros - Os parâmetros da requisição.
 * @param {unknown} payload - O payload da requisição.
 * @returns {Promise<any>}
 */
  private tentarFazerRequisicao (
    parametros: Record<string, unknown>,
    payload: unknown
  ) {
    const controladorDeCancelamentoDeRequisicao = criarControladorDeCancelamentoDeRequisicao()
    this.commit('adicionarControladorDeCancelamentoDeRequisicao', controladorDeCancelamentoDeRequisicao)

    const configuracoesRequisicao = {
      signal: controladorDeCancelamentoDeRequisicao.signal
    }
    const loteParaCarregar = parametros.load as CategoriaDeLotesDaFolha

    if (loteParaCarregar === SubRotaFolhaParcial.Pts) {
      return this.repositorioFolha.obterLoteDeMarcacoesDasLinhas(payload as IRequestFolhaParcialSubRotaBase, configuracoesRequisicao)
    } else if (loteParaCarregar === SubRotaFolhaParcial.Djs) {
      return this.repositorioFolha.obterLoteDeJornadaDiariasDasLinhas(payload as IRequestFolhaParcialSubRotaBase, configuracoesRequisicao)
    } else if (loteParaCarregar === SubRotaFolhaParcial.Whbs) {
      return this.repositorioFolha.obterLoteDeBancoDeHorasDasLinhas(payload as IRequestFolhaParcialSubRotaBase, configuracoesRequisicao)
    } else if (loteParaCarregar === SubRotaFolhaParcial.Approvals) {
      return this.repositorioFolha.obterLoteDeAprovacoesDasLinhas(payload as IRequestFolhaParcialSubRotaBase, configuracoesRequisicao)
    } else if (loteParaCarregar === SubRotaFolhaParcial.Justifies) {
      return this.repositorioFolha.obterLoteDeJustificativasDasLinhas(payload as IRequestLoteDeJustificativasDasLinhas, configuracoesRequisicao)
    } else {
      throw new Error(`Categoria de lotes da folha inválido: ${loteParaCarregar}`)
    }
  }

  /**
   * Obtém os IDs únicos (lista sem repetição) dos empregados das linhas
   * @param {Line[]} linhas - As linhas a serem processadas.
   * @returns {string[]}
   */
  static obterIdsUnicosDeEmpregados (linhas: Line[]): string[] {
    try {
      const empregadosIdsSet = new Set(linhas.map(linha => {
        if (!linha.employee.id) {
          throw new Error('Empregado sem ID')
        }
        return linha.employee.id
      }))
      return [...empregadosIdsSet]
    } catch (error) {
      FiltradorDeErros.capturarErro(error as Error)
      return []
    }
  }

  /**
 * Define os lotes de carregamento, entregando uma matriz em que cada subindice é uma lista cheia de ids que serão usados para carregar
 * @param {string[]} listaDeIds - A lista de IDs.
 * @param {number} quantidadeDeItensNoLote - A quantidade de itens por lote.
 * @returns {string[][]}
 */
  static definirLotesDeCarregamento (listaDeIds: string[], quantidadeDeItensNoLote: number): string[][] {
    return quantidadeDeItensNoLote ? montarItemsDaListaEmBlocos(listaDeIds, quantidadeDeItensNoLote) : [listaDeIds]
  }

  /**
 * Filtra as linhas dos empregados inclusos na lista de IDs.
 * @param {Line[]} linhas - As linhas a serem filtradas.
 * @param {string[]} listaDeIdsEmpregados - A lista de IDs dos empregados.
 * @returns {Line[]}
 */
  static filtrarLinhasDosEmpregadosInclusosNaListaDeIds (linhas: Line[], listaDeIdsEmpregados: string[]): Line[] {
    return linhas.filter(linha => linha.id && listaDeIdsEmpregados.includes(linha.employee.id))
  }

  /**
 * Carrega o primeiro lote.
 * @param {DatasLimitesRequisicao} parametrosRequisicao - Os parâmetros da requisição.
 * @param {string[][]} loteDeIds - O lote de IDs.
 * @returns {Promise<PayloadLoteDeCarregamento | DatasLimitesRequisicao>}
 */
  private async carregarPrimeiroLote (
    parametrosRequisicao: DatasLimitesRequisicao,
    loteDeIds: string[][]
  ): Promise<PayloadLoteDeCarregamento | DatasLimitesRequisicao> {
    const {
      linhasDoLote,
      payloadDoLote
    } = await this.carregarLote(parametrosRequisicao, this.obterListaDeIdsPrimeiroLote(loteDeIds))
    await this.carregarLoteAninhado(this.dadosAninhados, payloadDoLote, linhasDoLote)
    return payloadDoLote
  }

  /**
   * Carrega os demais lotes.
   * @param {DatasLimitesRequisicao} parametrosRequisicao - Os parâmetros da requisição.
   * @param {string[][]} loteDeIds - O lote de IDs.
   * @returns {Promise<void>}
   */
  private async carregarDemaisLotes (
    parametrosRequisicao: DatasLimitesRequisicao,
    loteDeIds: string[][]
  ): Promise<void> {
    const requisicoes = this.obterMatrizDeIdsDemaisLotes(loteDeIds).map(loteParticionado => {
      return this.carregarLote(parametrosRequisicao, loteParticionado)
    })

    const resultados = await Promise.allSettled(requisicoes)

    resultados
      .filter(resultadoDoLote => resultadoDoLote.status === 'fulfilled')
      .map(resultadosDoLote => {
        if ('value' in resultadosDoLote && resultadosDoLote.value) {
          const valor = resultadosDoLote.value as { linhasDoLote: Line[]; payloadDoLote: PayloadLoteDeCarregamento; }
          const {
            linhasDoLote,
            payloadDoLote
          } = valor
          return this.carregarLoteAninhado(this.dadosAninhados, payloadDoLote, linhasDoLote)
        }
        return null
      })
      .filter(Boolean)
  }

  private async carregarLote (
    parametrosRequisicao: DatasLimitesRequisicao,
    loteDeIds: string[]
  ) {
    const linhasDoLote = CarregadoraDeLotesFolha.filtrarLinhasDosEmpregadosInclusosNaListaDeIds(this.state.lines, loteDeIds)
    const payloadDoLote = {
      ...parametrosRequisicao,
      employees: loteDeIds,
      lines: linhasDoLote.map(linha => linha.id) as string[],
      infoToRefetch: null
    }

    await this.registrarRequisicaoCommitando(payloadDoLote, this.mergearMarcacoes, this.categoriaSendoCarregada)

    return {
      linhasDoLote,
      payloadDoLote
    }
  }

  private async carregarLoteAninhado (
    dadosAninhados: DadosAninhadosParaCarregamento[],
    parametrosDeRequisicaoAninhada: ParametrosDeRequisicaoAninhada,
    linhasDoLote: Line[]
  ) {
    dadosAninhados.map(dados => {
      const dicionarioComFuncaoDeCarregamento = this.obterFuncaoMapeadoraDeDadosAninhadosPorCategoria()
      const categoria = this.categoriaSendoCarregada as keyof FuncaoMapeadora
      const categoriaAninhada = dados.load
      const mergearMarcacoesAinhados = dados.mergeTms

      const parametrosDeRequisicoesAninhadas = {
        ...parametrosDeRequisicaoAninhada,
        ...dicionarioComFuncaoDeCarregamento[categoria](linhasDoLote)
      }

      return this.registrarRequisicaoCommitando(parametrosDeRequisicoesAninhadas, mergearMarcacoesAinhados, categoriaAninhada)
    })
  }

  private definirCarregamentosDasInformacoesDasLinhas (estadoDeCarregamento: boolean) {
    const estadosDeCarregamentoPorCategoria = { [this.categoriaSendoCarregada]: estadoDeCarregamento }
    this.dadosAninhados.forEach(() => { estadosDeCarregamentoPorCategoria[this.categoriaSendoCarregada] = estadoDeCarregamento })
    return estadosDeCarregamentoPorCategoria
  }

  private formatarParametrosParaRequisicao (): DatasLimitesRequisicao {
    return {
      startDate: formatarDataParaEnvio(moment(this.dataInicial)),
      endDate: formatarDataParaEnvio(moment(this.dataFinal))
    }
  }

  private filtrarLinhasQuePossuemJornadas (linhas: Line[]) {
    return linhas.map(linha => linha.dayJourney?.id).filter(Boolean)
  }

  private iniciarEstadoDeCarregamento () {
    this.commit('loading', this.definirCarregamentosDasInformacoesDasLinhas(true))
  }

  private pararEstadoDeCarregamento () {
    this.commit('loading', this.definirCarregamentosDasInformacoesDasLinhas(false))
  }

  private obterFuncaoMapeadoraDeDadosAninhadosPorCategoria (): FuncaoMapeadora {
    return {
      djs: (linhas: Line[]) => {
        return { djs: this.filtrarLinhasQuePossuemJornadas(linhas) }
      },
      pts: (linhas: Line[]) => {
        return { pts: linhas }
      },
      tms: (linhas: Line[]) => {
        return { tms: linhas }
      },
      justifies: (linhas: Line[]) => {
        return { justifies: linhas }
      },
      whbs: (linhas: Line[]) => {
        return { whbs: linhas }
      },
      approvals: (linhas: Line[]) => {
        return { approvals: linhas }
      }
    }
  }

  private obterListaDeIdsPrimeiroLote (matrizDeIds: string[][]): string[] {
    return matrizDeIds[0]
  }

  private obterMatrizDeIdsDemaisLotes (matrizDeIds: string[][]): string[][] {
    return matrizDeIds.slice(1, matrizDeIds.length)
  }
}

const criarcontroladorDeCancelamento = () => {
  const controller = new AbortController()
  return controller
}

let controladorCarregamentoFolha: AbortController | null = null

export class FolhaServico extends ServicoAbstrato<FolhaRepositorio, IStateFolha> {
  store!: Store<IStateFolha>

  // eslint-disable-next-line no-useless-constructor
  constructor (repositorio: FolhaRepositorio, store: Store<IStateFolha>) {
    super(repositorio, store)
  }

  carregarLoteDaFolha (
    categoriaSendoCarregada: CategoriaDeLotesDaFolha,
    dadosAninhados: DadosAninhadosParaCarregamento[],
    quantidadeDeItensNoLote: number,
    dataInicial: string,
    dataFinal: string,
    mergearMarcacoes: boolean
  ): Promise<void> {
    return new CarregadoraDeLotesFolha(
      this.repositorio,
      this.store.commit,
      this.store.state,
      categoriaSendoCarregada,
      dadosAninhados,
      quantidadeDeItensNoLote,
      dataInicial,
      dataFinal,
      mergearMarcacoes
    ).carregar()
  }

  async carregarFolha (
    dataInicial: string,
    dataFinal: string,
    pontosPorLocalDeTrabalho: boolean,
    locaisDeTrabalho: string[],
    empregados: string[],
    gruposDeUsuarios: string[]
  ) {
    this.store.commit('cancelarTodasAsRequisicoesPendentes')
    const controller = criarcontroladorDeCancelamento()
    this.store.commit('adicionarControladorDeCancelamentoDeRequisicao', controller)
    this.store.commit('resetLines')

    const payloadGenerico = {
      ptsByCompany: pontosPorLocalDeTrabalho,
      startDate: dataInicial,
      endDate: dataFinal
    }

    const configuracoesRequisicao = {
      signal: controller.signal
    }

    const requisicao = () => {
      if (locaisDeTrabalho && locaisDeTrabalho.length) {
        return this.repositorio.obterLoteDeLinhasPorLocalDeTrabalho(
          { ...payloadGenerico, companies: locaisDeTrabalho },
          configuracoesRequisicao
        )
      } else if (empregados && empregados.length) {
        return this.repositorio.obterLoteDeLinhasPorEmpregado(
          { ...payloadGenerico, employees: empregados },
          configuracoesRequisicao
        )
      } else if (gruposDeUsuarios && gruposDeUsuarios.length) {
        return this.repositorio.obterLoteDeLinhasPorGrupo(
          { ...payloadGenerico, userGroups: gruposDeUsuarios },
          configuracoesRequisicao
        )
      }
    }

    const resultado = await requisicao()
    this.store.commit('state', resultado?.data.success)
    this.store.commit('loading', { lines: false })
  }

  /**
   * Carrega os dados da folha utilizando o novo modelo de API (V2 - Unificação das APIs).
   * Responsável por buscar os dias, transformar os dados para o formato legado
   * e gerencia o recálculo assíncrono de dias pendentes (Lazy Loading)
   */
  async carregarFolhaV2({
    dia,
    mes,
    ano,
    // pontosPorLocalDeTrabalho,
    locaisDeTrabalho,
    empregados,
    gruposDeUsuarios
  }: IPayloadDiasDaFolhaPorEntidade) {
    controladorCarregamentoFolha?.abort()
    controladorCarregamentoFolha = new AbortController()
    const { signal } = controladorCarregamentoFolha
    try {
      this.store.commit('cancelarTodasAsRequisicoesPendentes')
      this.store.commit('resetLines')

      let payloadDiasDaFolha: Partial<IRequestDiasDaFolhaPorEntidade> = {
        // ptsByCompany: pontosPorLocalDeTrabalho, // TODO Othon disse que vai subir isso depois
        empregados_id: [],
        locais_de_trabalho_id: [],
        grupo_de_usuarios_id: [],
        incluir_abonos: store.state.userInfo.compMan.hasArbTms,
      }

      if (locaisDeTrabalho && locaisDeTrabalho.length) {
        payloadDiasDaFolha = {
          ...payloadDiasDaFolha,
          locais_de_trabalho_id: locaisDeTrabalho,
          dia,
          mes,
          ano
        }
      } else if (empregados && empregados.length) {
        payloadDiasDaFolha = {
          ...payloadDiasDaFolha,
          empregados_id: empregados,
          mes,
          ano
        }
      } else if (gruposDeUsuarios && gruposDeUsuarios.length) {
        payloadDiasDaFolha = {
          ...payloadDiasDaFolha,
          grupo_de_usuarios_id: gruposDeUsuarios,
          dia,
          mes,
          ano
        }
      } else {
        payloadDiasDaFolha = {
          ...payloadDiasDaFolha,
          dia,
          mes,
          ano
        }
      }

      // Iniciar ambas as promises em paralelo
      const resultadoDiasDaFolha = await this.carregarDiasDaFolha(payloadDiasDaFolha, signal)

      const resultadoTransformado = transformarDadosDaNovaApiParaAntiga(resultadoDiasDaFolha)

      // se a visualização for de empregados e houver dias pendentes, resolvemos de uma vez só.
      // São duas pendências distintas, com rotas próprias: a jornada marcada para ser
      // regerada (o criador de jornadas mudou) e o dia com cálculo sujo. Quem vai ser
      // regerado sai da lista de recálculo — a rota de regeração já devolve o dia
      // recalculado, então pedir os dois seria trabalho repetido.
      const linhasParaRegerarJornada = resultadoDiasDaFolha.filter(jornadaPrecisaSerRegerada)
      const chavesParaRegerarJornada = new Set(
        linhasParaRegerarJornada.map((linha: DiaDaFolhaDados) => gerarChaveLinhaRecalculo(linha.empregado_id, linha.dia))
      )
      const linhasParaRecalcular = resultadoDiasDaFolha.filter((linha: DiaDaFolhaDados) =>
        linha.precisa_recalcular && !chavesParaRegerarJornada.has(gerarChaveLinhaRecalculo(linha.empregado_id, linha.dia))
      )
      const linhasPendentes = [...linhasParaRegerarJornada, ...linhasParaRecalcular]

      if (empregados && empregados.length && linhasPendentes.length > 0) {
        this.adicionarLinhasNaListaDeRecalculando(linhasPendentes)
        Promise.all([
          this.regerarJornadasQuePrecisamLazyLoading(linhasParaRegerarJornada),
          this.recalcularDiasQuePrecisamLazyLoading(linhasParaRecalcular)
        ])
          .then(() => {
            // dispara a verificação para jornadas ainda não geradas (Aguarde, atualizando..)
            this.store.dispatch('verificarDiasEJornadasFaltantesNaFolha', {
              indices: null,
            });
          });
        // this.store.commit('sheet/atualizarAndamentoRecalculo', { recalculoEmAndamento: true }, { root: true })

        // try {
        //     await this.recalcularDiasQuePrecisamLazyLoading(linhasParaRecalcular)
        // } finally {
        //     this.store.commit('sheet/atualizarAndamentoRecalculo', { recalculoEmAndamento: false }, { root: true })
        // }
      }

      // copiando objeto sem referência para commit do state não modificar o objeto original, precisa estar igual na hora de recalcular os dias que precisam
      const resultadoParcial = structuredClone(resultadoTransformado)

      if (signal.aborted) {
        return
      }

      this.store.commit('popularLinhas', {
        lines: resultadoParcial.linhas,
        employees: resultadoParcial.empregados
      })

      // filtra linhas que têm pontos não processados
      const indicesComPontosNaoProcessados = resultadoParcial.linhas.reduce((indices: number[], linha: any, index: number) => {
        const pontoNaoProcessado = linha.timelogs?.some((grupo: any[]) =>
            grupo.some(p => !p.processado)
        );

        if (pontoNaoProcessado) {
            indices.push(index);
        }
        return indices;
      }, []);

      if (indicesComPontosNaoProcessados.length > 0) {
        this.store.dispatch('verificarDiasEJornadasFaltantesNaFolha', {
            indices: indicesComPontosNaoProcessados,
        });
      }
      const dataDoPeriodoHeader = (this.store as any).rootState?.timesheet?.header?.dataDoPeriodo
      const { inicio, fim } = obterLimitesPeriodoParaHeader(
        resultadoParcial.inicioPeriodo,
        resultadoParcial.fimPeriodo,
        dia,
        mes,
        ano,
        dataDoPeriodoHeader
      )
      this.store.commit('timesheet/header/startDate', inicio, { root: true })
      this.store.commit('timesheet/header/endDate', fim, { root: true })
      this.removerLinhasDaListaDeRecalculando(resultadoDiasDaFolha)
      this.store.commit('loading', { lines: false, pts: false, djs: false, tms: false, justifies: false, whbs: false })

    } catch (error) {
      if (error instanceof CanceledError) {
        return;
      }
      FiltradorDeErros.capturarErro(error as Error)
      modalDeErro(i18n.t('sheets.Sheet.Sheet.tenteNovamenteMaisTardeOuContateOSuporte'))
      throw error
    }
  }

  /**
   * Método responsável por recalcular as linhas que precisam com fila de recalculo.
   * (a partir do intersection observer da folha)
   * @returns void
   */
  async recalcularComFilaDeRecalculo () {
    // this.store.commit('sheet/atualizarAndamentoRecalculo', { recalculoEmAndamento: true }, { root: true })
    try {
      const linhasParaRecalcular = await this.store.getters['linhasParaRecalcularNaFila']

    // this.store.commit('atualizarLinhasPara', { estado: 'recalculando', linhas: linhasParaRecalcular })
      if (linhasParaRecalcular.length === 0) {
        return
      }

      const payload = linhasParaRecalcular.map((linha: string) => {
        const [empregadoId, dia] = linha.split('=')
        return {
          empregado_id: empregadoId,
          dia: dia,
          precisa_recalcular: true
        }
      })

      const stateLines = this.store.state.lines;
      const indicesProcessados: number[] = [];

      payload.forEach((linhasParaRecalcular: DiaSemDiaDaFolha) => {
        const index = stateLines.findIndex(linha => linha.employee.id === linhasParaRecalcular.empregado_id && linha.data === linhasParaRecalcular.dia);
        if (index !== -1) {
          indicesProcessados.push(index);
        }
      });

      await this.recalcularDiasQuePrecisamLazyLoading(payload).then(() => {
        // dispara a verificação para jornadas ainda não geradas (Aguarde, atualizando..)
        this.store.dispatch('verificarDiasEJornadasFaltantesNaFolha', {
          indices: indicesProcessados,
        });
      });
    } finally {
      // this.store.commit('sheet/atualizarAndamentoRecalculo', { recalculoEmAndamento: false }, { root: true })
    }
  }

  /**
   * Realiza o recálculo de dias pendentes em lotes sequenciais (ver `processarLinhasEmLotes`).
   * @param {DiaSemDiaDaFolha[]} linhasParaRecalcular - Lista de identificadores de dias que precisam ser atualizados.
   */
  async recalcularDiasQuePrecisamLazyLoading(linhasParaRecalcular: DiaSemDiaDaFolha[]) {
    await this.processarLinhasEmLotes(linhasParaRecalcular, chunk =>
      this.recalcularDiasQuePrecisam({
        empregados_para_recalculo_por_dia: this.montarPayloadDiasParaRecalcular(chunk),
        incluir_abonos: store.state.userInfo.compMan.hasArbTms
      })
    )
  }

  /**
   * Regera as jornadas marcadas com `jornada_precisa_ser_regerada` nos mesmos lotes
   * do recálculo. A rota do back regenera a jornada a partir do criador de jornadas
   * e já devolve o dia recalculado, então não é preciso pedir o recálculo depois.
   * @param {DiaSemDiaDaFolha[]} linhasParaRegerar - Dias cuja jornada precisa ser regerada.
   */
  async regerarJornadasQuePrecisamLazyLoading(linhasParaRegerar: DiaSemDiaDaFolha[]) {
    await this.processarLinhasEmLotes(linhasParaRegerar, chunk =>
      this.regerarJornadasQuePrecisam({
        empregados_para_regeracao_por_dia: agruparDiasPorEmpregado(chunk),
        incluir_abonos: store.state.userInfo.compMan.hasArbTms
      })
    )
  }

  /**
   * Gera a jornada dos dias que o recálculo sinalizou com `jornada_precisa_ser_gerada`,
   * nos mesmos lotes do recálculo. A rota do back gera e já devolve o dia calculado:
   * não há polling. O dia cuja geração não criou nada volta "Sem jornada".
   * @param {DiaSemDiaDaFolha[]} linhasParaGerar - Dias sem jornada sinalizados pelo back.
   */
  async gerarJornadasQueFaltamLazyLoading(linhasParaGerar: DiaSemDiaDaFolha[]) {
    await this.processarLinhasEmLotes(linhasParaGerar, chunk =>
      this.gerarJornadasQueFaltam({
        empregados_para_geracao_por_dia: agruparDiasPorEmpregado(chunk),
        incluir_abonos: store.state.userInfo.compMan.hasArbTms
      })
    )
  }

  /**
   * Percorre os dias pendentes em lotes sequenciais (chunks) de 15 itens, delegando a
   * chamada de cada lote a `executarLote`. Gerencia o ciclo de vida visual das linhas
   * (recalculando -> concluído/erro), transforma a resposta da API e atualiza a store
   * progressivamente à medida que cada lote termina, evitando sobrecarga na rede.
   * @param {DiaSemDiaDaFolha[]} linhas - Dias a processar.
   * @param executarLote - Chamada de API do lote (recálculo ou regeração de jornada).
   */
  private async processarLinhasEmLotes(
    linhas: DiaSemDiaDaFolha[],
    executarLote: (chunk: DiaSemDiaDaFolha[]) => Promise<DiaDaFolhaDados[]>
  ) {
    const tamanhoChunk = 15;
    for (let indice = 0; indice < linhas.length; indice += tamanhoChunk) {
      const chunk = linhas.slice(indice, indice + tamanhoChunk);
      const linhasEmProcessamento: string[] = []
      /**
       * Adiciona as linhas que estão em processamento para atualizar o estado das linhas
       */
      chunk.forEach(linhaPayload => {
        const empregadoId = linhaPayload.empregado_id
        const dia = linhaPayload.dia
        const chave = gerarChaveLinhaRecalculo(empregadoId, dia)

        linhasEmProcessamento.push(chave)
      })

      this.store.commit('atualizarLinhasPara', { estado: 'recalculando', linhas: linhasEmProcessamento })

      await executarLote(chunk)
        .then(linhas => {
          this.removerLinhasDaListaDeRecalculando(linhas)
          return linhas
        })
        .then(transformarDadosDaNovaApiParaAntiga)
        .then(recalculados => {

          // Se não houver linhas, não é necessário atualizar o estado da folha
          if (recalculados.linhas.length === 0) {
            return
          }

          this.store.commit('atualizarMultiplasLinhas', recalculados.linhas)

          const linhasConcluidas: string[] = []
          const linhasErroAoRecalcular: string[] = []

          /**
           * Verifica se todas as linhas que foram solicitadas foram processadas com sucesso
           * separa os identificadores (chave) das linhas em concluidas e com erro, para atualizar o estado
           * das linhas e para o usuário conseguir recalcular as linhas que precisam unitariamente
           */
          chunk.forEach(linhaPayload => {
            const empregadoId = linhaPayload.empregado_id
            const dia = linhaPayload.dia
            const chave = gerarChaveLinhaRecalculo(empregadoId, dia)

            if (recalculados.linhas.some((linha: Linha) => linha.employee.id === empregadoId && linha.data === dia)) {
              linhasConcluidas.push(chave)
            } else {
              linhasErroAoRecalcular.push(chave)
            }
          })


          this.store.commit('atualizarLinhasPara', { estado: EstadosLinhaParaRecalcularEnum.Concluido, linhas: linhasConcluidas })
          this.store.commit('atualizarLinhasPara', { estado: EstadosLinhaParaRecalcularEnum.ErroAoRecalcular, linhas: linhasErroAoRecalcular })
        })
        .catch((error) => {
          if (error instanceof CanceledError) {
            return;
          }
          const linhasErroAoRecalcular: string[] = []
          chunk.forEach(linhaPayload => {
            const empregadoId = linhaPayload.empregado_id
            const dia = linhaPayload.dia
            const chave = gerarChaveLinhaRecalculo(empregadoId, dia)
            linhasErroAoRecalcular.push(chave)
          })

          this.store.commit('atualizarLinhasPara', { estado: EstadosLinhaParaRecalcularEnum.ErroAoRecalcular, linhas: linhasErroAoRecalcular })
        })
    }
  }

  async carregarDiasDaFolha(payload: Partial<IRequestDiasDaFolhaPorEntidade>, signal?: AbortSignal) {
    try {
      const resultado = await this.repositorio.obterDiasDaFolhaPorEntidade(payload as IRequestDiasDaFolhaPorEntidade, { signal })
      return resultado.data.dados
    } catch (error) {
      FiltradorDeErros.capturarErro(error as Error)
      throw error
    }
  }

  async recalcularDiasQuePrecisam(data: any) {
    try {
      const controller = criarcontroladorDeCancelamento()
      this.store.commit('adicionarControladorDeCancelamentoDeRequisicao', controller)

      const configuracoesRequisicao = {
        signal: controller.signal
      }

      const resultado = await this.repositorio.recalcularDiasQuePrecisam(data, configuracoesRequisicao)
      return resultado.data.dados
    } catch (error) {
      FiltradorDeErros.capturarErro(error as Error)
      throw error
    }
  }

  async regerarJornadasQuePrecisam(data: IRequestRegerarJornadasQuePrecisam) {
    try {
      const controller = criarcontroladorDeCancelamento()
      this.store.commit('adicionarControladorDeCancelamentoDeRequisicao', controller)

      const configuracoesRequisicao = {
        signal: controller.signal
      }

      const resultado = await this.repositorio.regerarJornadasQuePrecisam(data, configuracoesRequisicao)
      return resultado.data.dados
    } catch (error) {
      FiltradorDeErros.capturarErro(error as Error)
      throw error
    }
  }

  async gerarJornadasQueFaltam(data: IRequestGerarJornadasQueFaltam) {
    try {
      const controller = criarcontroladorDeCancelamento()
      this.store.commit('adicionarControladorDeCancelamentoDeRequisicao', controller)

      const configuracoesRequisicao = {
        signal: controller.signal
      }

      const resultado = await this.repositorio.gerarJornadasQueFaltam(data, configuracoesRequisicao)
      return resultado.data.dados
    } catch (error) {
      FiltradorDeErros.capturarErro(error as Error)
      throw error
    }
  }

  /**
   * Processa pontos que estão com a flag 'processado: false'
   * constrói o mapa de pontos por empregado com base na linha
   */
  async processarPontosQuePrecisam(linhasComPontosNaoProcessados: Line[]) {
    try {
      const controller = criarcontroladorDeCancelamento()
      this.store.commit('adicionarControladorDeCancelamentoDeRequisicao', controller)

      const datasPorEmpregado: Record<EmpregadoId, string[]> = {}

      linhasComPontosNaoProcessados.forEach(linha => {
        const dataFormatada = formatarDataParaEnvio(linha.data)
        if (!datasPorEmpregado[linha.employee.id]) {
          datasPorEmpregado[linha.employee.id] = []
        }
        if (!datasPorEmpregado[linha.employee.id].includes(dataFormatada)) {
          datasPorEmpregado[linha.employee.id].push(dataFormatada)
        }
      })

      await this.repositorio.processarPontosDaFolha(
        { datas_por_empregado: datasPorEmpregado },
        { signal: controller.signal }
      )
    } catch (error) {
      FiltradorDeErros.capturarErro(error as Error)
      throw error
    }
  }

  /**
   * Método responsável por montar o payload para recalcular os dias que precisam.
   * @param linhas - Linhas da folha.
   * @returns Empregados para recalcular por dia.
   */
  montarPayloadDiasParaRecalcular(linhas: DiaSemDiaDaFolha[]): Record<EmpregadoId, DataEmFormatoISO[]> {
    return agruparDiasPorEmpregado(linhas.filter(linha => linha.precisa_recalcular))
  }

  adicionarLinhasNaListaDeRecalculando(linhas: DiaDaFolhaDados[]) {
    const linhasParaRecalcular: string[] = []
    linhas.filter(linha => linha.precisa_recalcular || jornadaPrecisaSerRegerada(linha)).forEach(linha => {
      const chave = gerarChaveLinhaRecalculo(linha.empregado_id, linha.dia)
      linhasParaRecalcular.push(chave)
    })
    this.store.commit('adicionarLinhaParaRecalcular', { linhas: linhasParaRecalcular })
  }

  removerLinhasDaListaDeRecalculando(linhas: DiaDaFolhaDados[]) {
    const linhasParaRemover: string[] = []
    linhas.filter(linha => linha.precisa_recalcular === false && !jornadaPrecisaSerRegerada(linha)).forEach(linha => {
      const chave = gerarChaveLinhaRecalculo(linha.empregado_id, linha.dia)
      linhasParaRemover.push(chave)
    })

    if (linhasParaRemover.length === 0) {
      return
    }

    this.store.commit('atualizarLinhasPara', { estado: EstadosLinhaParaRecalcularEnum.Concluido, linhas: linhasParaRemover })
  }

  async verificarDiaDafolha(payload: IRequestVerificarDia){
    try {
      await this.repositorio.verificarDiaDafolha(payload)
    } catch (error) {
      FiltradorDeErros.capturarErro(error as Error)
      throw error
    }
  }

  async carregarAcumuladoBancoDeHoras (empregado_id: EmpregadoId, dataDeReferencia: Date) {
    try {
      this.store.commit('timesheet/carregandoAcumuladoBancoDeHoras', true, { root: true })

      // Lógica de transformação dos parâmetros
      const mes = moment(dataDeReferencia).month() + 1 // +1 porque os meses em moment.js são de 0 a 11
      const ano = moment(dataDeReferencia).year()

      const params = { empregado_id, mes, ano }
      const resultado = await this.repositorio.obterAcumuladoBancoDeHoras(params)
      const detalhesDosApontamentosConvertidos = transformarDetalhesDeApontamentos(resultado.data.detalhes_de_apontamentos)

      const dadosDeAcumuladoDeBancoDeHorasFormatados = {
        overal: {
          ...resultado.data.acumulado_geral,
          manterSaldoMensal: resultado.data.manter_saldo_mensal,
          showDetails: resultado.data.mostrar_detalhes,
          window: null,
          titles: detalhesDosApontamentosConvertidos
        },
        monthly: {
          ...resultado.data.acumulado_mensal,
          manterSaldoMensal: resultado.data.manter_saldo_mensal,
          showDetails: resultado.data.mostrar_detalhes,
          window: null,
          titles: detalhesDosApontamentosConvertidos
        }
      };
      return dadosDeAcumuladoDeBancoDeHorasFormatados

    } catch (error) {
      FiltradorDeErros.capturarErro(error as Error)
      this.store.commit('timesheet/sumWhb', { overal: null, monthly: null }, { root: true }) // Limpa em caso de erro
      throw error
    } finally {
      this.store.commit('timesheet/carregandoAcumuladoBancoDeHoras', false, { root: true })
    }
  }
}
